/**
 * Patient availability report (2.5): what the welcome call captured and what
 * anyone has learned since — the days and windows the patient is available — and
 * every future planned visit checked against it. Every constraint is a
 * requirement; there is no hard-versus-soft class (script author, discussion
 * turn 67).
 *
 * The outside list is the same check the `availabilityConflict` worklist kind is
 * built from, so the page and the worklist can never disagree about which visits
 * are wrong; the alerts panel carries the ones inside the row lead, this carries
 * the whole cert period. Edit constraints stays where it was.
 */
import { useMemo, useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { WelcomeCallWizard } from "../../components/WelcomeCallWizard";
import { dateKeyStart, formatInBranchZone, selectDemoNow, toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { availabilityCheck, parseDays, parseWindow, visitTypeLabel } from "../../engine/constraints";
import type { PatientConstraint } from "@caredna-marlin/utils/homeHealthDemo/state";
import { useDemoState } from "../../store/useDemoStore";
import { useBranchClock, useDemoBranchClock } from "../../components/branchClock";
import { atWall, type TimeSentence } from "@caredna-marlin/utils/homeHealthDemo/engine/timeText";

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DEFAULT_WINDOW: [number, number] = [8 * 60, 18 * 60];

export function PatientAvailabilityReport({ patientRef }: { patientRef: string }) {
  const state = useDemoState();
  const tz = state.config.branch.timeZone;
  const today = toDateKey(selectDemoNow(state, new Date()), tz);
  const [wizard, setWizard] = useState<"welcomeCall" | "edit" | null>(null);
  const patient = state.patients[patientRef];
  const pendingCall = useMemo(
    () => Object.values(state.holds).some((h) => h.patientRef === patientRef && h.status === "held" && state.readiness[h.id]?.readinessCall !== undefined && state.readiness[h.id]?.readinessCall !== "done"),
    [state.holds, state.readiness, patientRef],
  );
  const check = useMemo(() => availabilityCheck(state, patientRef, today), [state, patientRef, today]);
  // Conversion placed the start of care inside the window the welcome call captured.
  const placed = useMemo(() => {
    const holdIds = new Set(Object.values(state.holds).filter((h) => h.patientRef === patientRef).map((h) => h.id));
    return Object.values(state.annotations).find((a) => a.holdId && holdIds.has(a.holdId) && a.placedInsideWindow)?.placedInsideWindow;
  }, [state.annotations, state.holds, patientRef]);
  if (!patient) return null;

  const constraints = patient.constraints;
  const days = availableDays(constraints);
  const windows = constraints.filter((c) => c.kind === "availabilityWindow").map((c) => parseWindow(c.value)).filter((w): w is [number, number] => !!w);
  const window = windows[0] ?? DEFAULT_WINDOW;
  const fmtDay = (d: string) => formatInBranchZone(dateKeyStart(d, tz), tz, "EEE MMM d");
  const clock = useBranchClock(tz);

  return (
    <Box data-availability-report={patientRef}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="h6">Patient availability</Typography>
        <Box sx={{ flex: 1 }} />
        {pendingCall ? (
          <Button size="small" variant="contained" onClick={() => setWizard("welcomeCall")}>
            Welcome call
          </Button>
        ) : (
          <Button size="small" variant="outlined" onClick={() => setWizard("edit")}>
            Edit
          </Button>
        )}
      </Stack>

      {constraints.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nothing captured yet.
        </Typography>
      ) : (
        // The grid is the report. The sentences a person wrote down live in the
        // product's own patient notes at the top of the page, and SMS consent is
        // off the screen altogether (Pete, 2026-09-22).
        <DaysGrid days={days} window={window} />
      )}

      {placed && (
        <Alert severity="info" variant="outlined" sx={{ mt: 1.5, py: 0 }}>
          Start of care placed at {placed.placedStart} inside the window from the welcome call ({placed.window}); the hold started at {placed.holdStart}.
        </Alert>
      )}

      {/* **The conflicts, and nothing about them** (Pete, 2026-09-21). What was
          here besides: a count of how many future visits fit, a sentence
          explaining which of these the alerts panel also carries, and a Notes
          line for what were then the soft constraints. Each was a thing to read before getting
          to the thing worth reading. A visit that does not fit says so itself,
          with its day, its type, its clinician and the window it breaks; a
          patient whose visits all fit says nothing at all. */}
      {check.outside.length > 0 && (
        <Box sx={{ mt: 2 }} data-availability-check>
          {check.outside.map((c) => (
            <Alert key={c.visit.id} severity="warning" variant="outlined" sx={{ mt: 0.75, py: 0 }} data-outside-visit={c.visit.id}>
              {fmtDay(c.placement.targetDate)} · {visitTypeLabel(c.visit.visitTypeKind)}
              {c.visit.providerHandle ? ` · ${state.roster[c.visit.providerHandle]?.displayName ?? c.visit.providerHandle}` : ""} — {c.violations.map((v) => clock.sentence(v.reason)).join("; ")}.
            </Alert>
          ))}
        </Box>
      )}

      {wizard && <WelcomeCallWizard open onClose={() => setWizard(null)} patientRef={patientRef} mode={wizard} />}
    </Box>
  );
}

/** ISO weekdays the patient is available; every day when no day rule was captured. */
export function availableDays(constraints: PatientConstraint[]): number[] {
  const rules = constraints.filter((c) => c.kind === "availabilityDays").map((c) => parseDays(c.value));
  if (rules.length === 0) return [1, 2, 3, 4, 5, 6, 7];
  return [1, 2, 3, 4, 5, 6, 7].filter((d) => rules.every((r) => r.includes(d)));
}

function hhmm(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

function DaysGrid({ days, window }: { days: number[]; window: [number, number] }) {
  // `hhmm` turns the report's minutes back into a branch wall clock; the clock phrases it.
  const clock = useDemoBranchClock();
  const span = `${clock.wall(hhmm(window[0]))}–${clock.wall(hhmm(window[1]))}`;
  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 68px))", gap: 0.5, mt: 0.5 }}>
        {DAY_NAMES.map((name, i) => {
          const open = days.includes(i + 1);
          return (
            <Box key={name} data-availability-day={name} data-open={open ? "yes" : "no"} sx={{ border: "1px solid #e0e0e0", borderRadius: 0.5, textAlign: "center", py: 0.5, bgcolor: open ? "#e7f0e7" : "#f5f5f5", color: open ? "text.primary" : "text.disabled" }}>
              <Typography sx={{ fontSize: 11, fontWeight: 600 }}>{name}</Typography>
              <Typography sx={{ fontSize: 9.5 }}>{open ? span : "not available"}</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/** Plain words for the report; "Mon,Wed,Thu,Fri,Sat,Sun" reads as the day it leaves out. */
export function describeConstraint(constraint: PatientConstraint): TimeSentence {
  switch (constraint.kind) {
    case "availabilityDays": {
      const open = parseDays(constraint.value);
      const closed = [1, 2, 3, 4, 5, 6, 7].filter((d) => !open.includes(d));
      if (closed.length === 0) return "Any day";
      if (closed.length <= 2) return `Not ${closed.map((d) => DAY_NAMES[d - 1]).join(" or ")}`;
      return `Available ${open.map((d) => DAY_NAMES[d - 1]).join(", ")}`;
    }
    case "availabilityWindow": {
      const [from = "", to = ""] = constraint.value.split("-").map((x) => x.trim());
      return ["Available ", atWall(from), "-", atWall(to)];
    }
    case "language":
      return `Language: ${constraint.value}`;
    case "gender":
      return `${constraint.value === "F" ? "Female" : "Male"} clinician`;
    case "caregiver":
      return `Caregiver: ${constraint.value}`;
    case "smsConsent":
      return `SMS consent: ${constraint.value}`;
  }
}
